import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Suspense, lazy, Component } from 'react';

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', fontFamily:'sans-serif', gap:16, padding:24 }}>
          <div style={{ fontSize:'2rem' }}>⚠️</div>
          <div style={{ fontWeight:700, fontSize:'1.1rem', color:'#dc2626' }}>Page crashed</div>
          <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:8, padding:'12px 18px', maxWidth:600, fontSize:'0.85rem', color:'#991b1b', fontFamily:'monospace', wordBreak:'break-all' }}>
            {this.state.error?.message || String(this.state.error)}
          </div>
          <button style={{ padding:'8px 20px', background:'#7c3aed', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontWeight:600 }} onClick={() => window.location.reload()}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

import LoginPage       from './pages/Login';
import ForgotPassword  from './pages/ForgotPassword';
import ResetPassword   from './pages/ResetPassword';
import InvestorSignup  from './pages/InvestorSignup';
import MobileApp       from './mobile/MobileApp';
import Dashboard       from './pages/Dashboard';
import DoctorsPage     from './pages/Doctors';
import ActualsPage     from './pages/Actuals';
import PortalHome      from './pages/PortalHome';

const TerritoriesPage       = lazy(() => import('./pages/Territories'));
const TargetsPage           = lazy(() => import('./pages/Targets'));
const IncentiveLayout       = lazy(() => import('./pages/incentives/IncentiveLayout'));
const VisitsPage            = lazy(() => import('./pages/Visits'));
const UsersPage             = lazy(() => import('./pages/Users'));
const RolesPage             = lazy(() => import('./pages/Roles'));
const TravelPage            = lazy(() => import('./pages/Travel'));
const TravelPlanBuilder     = lazy(() => import('./pages/TravelPlanBuilder'));

const EventsPage            = lazy(() => import('./pages/Events'));
const ExpansionPage         = lazy(() => import('./pages/Expansion'));
const AccountsPage          = lazy(() => import('./pages/Accounts'));
const MasterDataPage        = lazy(() => import('./pages/MasterData'));
const SettingsPage          = lazy(() => import('./pages/Settings'));
const NotificationRulesPage = lazy(() => import('./pages/NotificationRules'));
const ReportsPage           = lazy(() => import('./pages/Reports'));
const MonthClosePage        = lazy(() => import('./pages/MonthClose'));
const AssumptionsPage       = lazy(() => import('./pages/Assumptions'));
const AuditLogPage          = lazy(() => import('./pages/AuditLog'));
const AttendancePage        = lazy(() => import('./pages/Attendance'));
const BDEPage              = lazy(() => import('./pages/BDE'));
const ExpensePoliciesPage   = lazy(() => import('./pages/ExpensePolicies'));
const ExpensesPage          = lazy(() => import('./pages/Expenses'));
const ExpenseApprovalsPage  = lazy(() => import('./pages/ExpenseApprovals'));
const FieldIntegrityPage    = lazy(() => import('./pages/FieldIntegrity'));
const GrowthBoardPage       = lazy(() => import('./pages/GrowthBoard'));
const EstimatesPage         = lazy(() => import('./pages/Estimates'));
const EstimateFormPage      = lazy(() => import('./pages/EstimateForm'));
const FinancePage           = lazy(() => import('./pages/Finance'));
const HRMastersPage         = lazy(() => import('./pages/HRMasters'));
const HierarchyPage         = lazy(() => import('./pages/Hierarchy'));
const HolidaysPage          = lazy(() => import('./pages/Holidays'));
const LeavePage             = lazy(() => import('./pages/Leave'));
const LifecyclePage         = lazy(() => import('./pages/Lifecycle'));
const EmployeeHomePage      = lazy(() => import('./pages/EmployeeHome'));
const SalesOrdersPage       = lazy(() => import('./pages/SalesOrders'));
const DealsPage             = lazy(() => import('./pages/Deals'));
const SaleFormPage          = lazy(() => import('./pages/SaleForm'));
const ConferencesPage       = lazy(() => import('./pages/Conferences'));
const CSEPage               = lazy(() => import('./pages/CSE'));
const WebsiteInboxPage      = lazy(() => import('./pages/WebsiteInbox'));
const SupportModulePage     = lazy(() => import('./pages/SupportModule'));
const CseConfigPage         = lazy(() => import('./pages/CseConfig'));
const EngineConfigPage      = lazy(() => import('./pages/EngineConfig'));
const InboxPage             = lazy(() => import('./pages/Inbox'));
const RevenueAttributionPage = lazy(() => import('./pages/RevenueAttribution'));
const CustomerPortalPage    = lazy(() => import('./pages/CustomerPortal'));
const ConferenceDetailPage  = lazy(() => import('./pages/Conferences').then(m => ({ default: m.ConferenceDetailPage })));
const DeletedRecordsPage        = lazy(() => import('./pages/DeletedRecords'));
const AdminDataCleanupPage      = lazy(() => import('./pages/AdminDataCleanup'));
const FieldPerformancePage      = lazy(() => import('./pages/FieldPerformance'));
const MyDayPage                 = lazy(() => import('./pages/MyDay'));
const DistributorsPage          = lazy(() => import('./pages/Distributors'));
const DistributorPerformanceBoardPage = lazy(() => import('./pages/DistributorPerformanceBoard'));
const DistributorOnboardPage    = lazy(() => import('./pages/DistributorOnboard'));
const DistributorInviteFormPage  = lazy(() => import('./pages/DistributorInviteForm'));
const DistributorInterestFormPage = lazy(() => import('./pages/DistributorInterestForm'));
const DistributorDetailPage     = lazy(() => import('./pages/DistributorDetail'));
const DistributorOrdersPage     = lazy(() => import('./pages/DistributorOrders'));
const DistributorOrderFormPage  = lazy(() => import('./pages/DistributorOrderForm'));
const DistributorSettlementsPage = lazy(() => import('./pages/DistributorSettlements'));
const DistributorSettlementDetailPage = lazy(() => import('./pages/DistributorSettlements').then(m => ({ default: m.DistributorSettlementDetail })));
const DistributorPortalPage     = lazy(() => import('./pages/DistributorPortal'));
const DistributorSupportPage    = lazy(() => import('./pages/DistributorSupport'));
const DocumentsPage             = lazy(() => import('./pages/Documents'));
const DistributorDocumentsPage  = lazy(() => import('./pages/DistributorDocuments'));
// DispatchDashboard moved to /inventory/* — kept here only as a redirect target
const StockAvailability         = lazy(() => import('./pages/StockAvailability'));
const TasksPage                 = lazy(() => import('./pages/Tasks'));
const BudgetLayout              = lazy(() => import('./pages/budget/BudgetLayout'));
const { MyFieldReport, TeamFieldReport, RepFieldReport } = { MyFieldReport: lazy(() => import('./pages/FieldReport').then(m => ({ default: m.MyFieldReport }))), TeamFieldReport: lazy(() => import('./pages/FieldReport').then(m => ({ default: m.TeamFieldReport }))), RepFieldReport: lazy(() => import('./pages/FieldReport').then(m => ({ default: m.RepFieldReport }))) };

// ── Portal modules ────────────────────────────────────────────────────
const DALayout                  = lazy(() => import('./pages/da/DALayout'));
const HRLayout                  = lazy(() => import('./pages/hr/HRLayout'));
const PayrollLayout             = lazy(() => import('./pages/payroll/PayrollLayout'));
const BooksLayout               = lazy(() => import('./pages/books/BooksLayout'));
const InventoryLayout           = lazy(() => import('./pages/inventory/InventoryLayout'));
const FeedbackLayout            = lazy(() => import('./pages/feedback/FeedbackLayout'));
const FeedbackPublicPage        = lazy(() => import('./pages/FeedbackPublic'));
const EnterpriseLayout          = lazy(() => import('./pages/enterprise/EnterpriseLayout'));
const InvestorLayout            = lazy(() => import('./pages/investor/InvestorLayout'));
const TendersLayout             = lazy(() => import('./pages/tenders/TendersLayout'));
const FirmwareLayout            = lazy(() => import('./pages/firmware/FirmwareLayout'));
const AMLayout                  = lazy(() => import('./pages/account-manager/AMLayout'));

function useAutoMobileRedirect() {
  const { user } = useAuth();
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const role = user?.role_slug || user?.role || '';
  const isFieldRole = ['sales_rep','rsm','bde'].includes(role);
  const isOnDesktopRoute = !window.location.pathname.startsWith('/m');
  if (user && isMobile && isFieldRole && isOnDesktopRoute) {
    window.location.replace('/m');
  }
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  useAutoMobileRedirect();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', gap:12, color:'var(--text-muted)' }}><div className="spinner"/>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const role = user.role_slug || user.role || '';

  // Distributor bypasses portal — goes straight to their portal
  if (role === 'distributor' && location.pathname === '/') {
    return <Navigate to="/distributor-portal" replace />;
  }

  // Investor bypasses portal home — goes straight to investor dashboard
  if (role === 'investor' && location.pathname === '/') {
    return <Navigate to="/dashboard" replace />;
  }

  // Firmware-only roles bypass portal home — goes straight to the firmware module
  if (['engineer','qa','ra_approver'].includes(role) && location.pathname === '/') {
    return <Navigate to="/firmware" replace />;
  }

  return children;
}

function MobileRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', color:'var(--text-muted)' }}>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const role = user?.role_slug || user?.role || '';
  if (['super_admin','admin','ceo','vp_sales'].includes(role)) return <Navigate to="/" replace />;
  return children;
}

// Blocks distributor role from sales-rep-only pages
function NoDistributor({ children }) {
  const { user } = useAuth();
  const role = user?.role_slug || user?.role || '';
  if (role === 'distributor') return <Navigate to="/distributor-portal" replace />;
  return children;
}

function PageFallback() {
  return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh', gap:12, color:'var(--text-muted)' }}><div className="spinner"/>Loading page…</div>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{ style:{ fontFamily:'DM Sans, sans-serif', fontSize:'0.875rem' } }}/>
        <Routes>
          {/* ── Public routes — no auth needed ── */}
          <Route path="/support"          element={<Suspense fallback={<PageFallback/>}><CustomerPortalPage/></Suspense>}/>
          <Route path="/login"            element={<LoginPage/>}/>
          <Route path="/forgot-password"  element={<ForgotPassword/>}/>
          <Route path="/reset-password"   element={<ResetPassword/>}/>
          <Route path="/distributor-invite/:token" element={<DistributorInviteFormPage/>}/>
          <Route path="/distributor-interest"       element={<DistributorInterestFormPage/>}/>
          <Route path="/investor-signup"            element={<InvestorSignup/>}/>
          {/* ── Public feedback form — no auth ── */}
          <Route path="/f/:shortCode"     element={<Suspense fallback={<PageFallback/>}><FeedbackPublicPage/></Suspense>}/>
          <Route path="/f"               element={<Suspense fallback={<PageFallback/>}><FeedbackPublicPage/></Suspense>}/>

          {/* ── Protected routes ── */}
          <Route path="/*" element={
            <PrivateRoute>
              <ErrorBoundary>
              <Suspense fallback={<PageFallback/>}>
                <Routes>
                  {/* ── Portal home — tile landing ── */}
                  <Route path="/"                   element={<PortalHome/>}/>
                  <Route path="/dashboard"           element={<Dashboard/>}/>
                  <Route path="/doctors"             element={<DoctorsPage/>}/>
                  <Route path="/accounts"            element={<AccountsPage/>}/>
                  <Route path="/expansion"           element={<ExpansionPage/>}/>
                  <Route path="/targets"             element={<TargetsPage/>}/>
                  <Route path="/incentives/*"        element={<IncentiveLayout/>}/>
                  <Route path="/actuals"             element={<ActualsPage/>}/>
                  <Route path="/deals"               element={<DealsPage/>}/>
                  <Route path="/sales-orders"        element={<SalesOrdersPage/>}/>
                  <Route path="/sales-orders/new"    element={<SaleFormPage/>}/>
                  <Route path="/sales-orders/:id"    element={<SaleFormPage/>}/>
                  <Route path="/visits"              element={<NoDistributor><VisitsPage/></NoDistributor>}/>
                  <Route path="/travel"              element={<NoDistributor><TravelPage/></NoDistributor>}/>
                  <Route path="/travel-plan/build"   element={<NoDistributor><TravelPlanBuilder/></NoDistributor>}/>
                  <Route path="/events"              element={<EventsPage/>}/>
                  <Route path="/territories"         element={<TerritoriesPage/>}/>
                  <Route path="/users"               element={<UsersPage/>}/>
                  <Route path="/roles"               element={<RolesPage/>}/>
                  <Route path="/master-data"         element={<MasterDataPage/>}/>
                  <Route path="/settings"            element={<SettingsPage/>}/>
                  <Route path="/notification-rules"  element={<NotificationRulesPage/>}/>
                  <Route path="/reports"             element={<ReportsPage/>}/>
                  <Route path="/month-close"         element={<MonthClosePage/>}/>
                  <Route path="/assumptions"         element={<AssumptionsPage/>}/>
                  <Route path="/audit-log"           element={<AuditLogPage/>}/>
                  <Route path="/attendance"          element={<AttendancePage/>}/>
                  <Route path="/bde"                  element={<BDEPage/>}/>
                  <Route path="/expense-policies"     element={<ExpensePoliciesPage/>}/>
                  <Route path="/expenses"             element={<ExpensesPage/>}/>
                  <Route path="/expense-approvals"    element={<ExpenseApprovalsPage/>}/>
                  <Route path="/field-integrity"      element={<FieldIntegrityPage/>}/>
                  <Route path="/growth-board"         element={<GrowthBoardPage/>}/>
                  <Route path="/estimates"            element={<EstimatesPage/>}/>
                  <Route path="/estimates/new"        element={<EstimateFormPage/>}/>
                  <Route path="/estimates/:id"        element={<EstimateFormPage/>}/>
                  <Route path="/finance"              element={<FinancePage/>}/>
                  <Route path="/hr-masters"           element={<HRMastersPage/>}/>
                  <Route path="/hierarchy"            element={<HierarchyPage/>}/>
                  <Route path="/holidays"             element={<HolidaysPage/>}/>
                  <Route path="/leave"                element={<LeavePage/>}/>
                  <Route path="/lifecycle"            element={<LifecyclePage/>}/>
                  <Route path="/conferences"          element={<ConferencesPage/>}/>
                  <Route path="/conferences/:id"      element={<ConferenceDetailPage/>}/>
                  <Route path="/support-tickets"      element={<CSEPage/>}/>
                  <Route path="/website-inbox"        element={<WebsiteInboxPage/>}/>
                  <Route path="/support-module"       element={<Suspense fallback={<PageFallback/>}><SupportModulePage/></Suspense>}/>
                  <Route path="/documents"            element={<DocumentsPage/>}/>
                  <Route path="/cse-config"           element={<CseConfigPage/>}/>
                  <Route path="/engine-config"        element={<EngineConfigPage/>}/>
                  <Route path="/inbox"               element={<InboxPage/>}/>
                  <Route path="/revenue-attribution" element={<RevenueAttributionPage/>}/>
                  <Route path="/deleted-records"           element={<DeletedRecordsPage/>}/>
                  <Route path="/admin/data-cleanup"        element={<AdminDataCleanupPage/>}/>
                  <Route path="/investor/*"                element={<Suspense fallback={<PageFallback/>}><InvestorLayout/></Suspense>}/>
                  <Route path="/firmware/*"                element={<Suspense fallback={<PageFallback/>}><FirmwareLayout/></Suspense>}/>
                  <Route path="/account-manager/*"         element={<Suspense fallback={<PageFallback/>}><AMLayout/></Suspense>}/>
                  <Route path="/distributors"              element={<DistributorsPage/>}/>
                  <Route path="/distributors/performance-board" element={<DistributorPerformanceBoardPage/>}/>
                  <Route path="/distributors/onboard"       element={<DistributorOnboardPage/>}/>
                  <Route path="/distributors/:id"          element={<DistributorDetailPage/>}/>
                  <Route path="/distributor-orders"        element={<DistributorOrdersPage/>}/>
                  <Route path="/distributor-orders/new"    element={<DistributorOrderFormPage/>}/>
                  <Route path="/distributor-orders/:id"    element={<DistributorOrderFormPage/>}/>
                  <Route path="/distributor-settlements"   element={<DistributorSettlementsPage/>}/>
                  <Route path="/distributor-settlements/:id" element={<DistributorSettlementDetailPage/>}/>
                  <Route path="/distributor-portal"         element={<DistributorPortalPage/>}/>
                  <Route path="/distributor-portal/support"   element={<DistributorSupportPage/>}/>
                  <Route path="/distributor-portal/documents" element={<DistributorDocumentsPage/>}/>
                  <Route path="/distributor-portal/:section"  element={<DistributorPortalPage/>}/>
                  {/* Retired dispatch route — redirect to inventory */}
                  <Route path="/dispatch-dashboard"       element={<Navigate to="/inventory/dispatch"   replace/>}/>
                  {/* Stock Availability lives outside /inventory/* on purpose — it's
                      granted broadly (sales_rep, bde, rsm, ...) via AppLayout's 'stock'
                      nav item, and nesting it under InventoryLayout previously meant
                      granting it also exposed the full Inventory module shell/nav to
                      roles that have no business in Vendors/Bills/BOM/Assembly/etc. */}
                  <Route path="/stock"                    element={<StockAvailability/>}/>
                  <Route path="/stock-admin"              element={<Navigate to="/inventory/items" replace/>}/>
                  <Route path="/tasks"                   element={<TasksPage/>}/>
                  <Route path="/budget/*"               element={<BudgetLayout/>}/>

                  {/* ── Field Activity Report ── */}
                  <Route path="/field-report"              element={<MyFieldReport/>}/>
                  <Route path="/field-report/team"         element={<TeamFieldReport/>}/>
                  <Route path="/field-report/user/:userId" element={<RepFieldReport/>}/>
                  <Route path="/field-performance"         element={<FieldPerformancePage/>}/>
                  <Route path="/my-day"                    element={<MyDayPage/>}/>

                  <Route path="/home"                 element={<EmployeeHomePage/>}/>

                  {/* ── Data Acquisition Module ── */}
                  <Route path="/data-acquisition/*"  element={<Suspense fallback={<PageFallback/>}><DALayout/></Suspense>}/>

                  {/* ── HR Module ── */}
                  <Route path="/hr/*"                element={<HRLayout/>}/>

                  {/* ── Payroll Module ── */}
                  <Route path="/payroll/*"           element={<PayrollLayout/>}/>

                  {/* ── Books Module ── */}
                  <Route path="/books/*"             element={<BooksLayout/>}/>

                  {/* ── Inventory Module ── */}
                  <Route path="/inventory/*"         element={<InventoryLayout/>}/>

                  {/* ── Feedback Module ── */}
                  <Route path="/feedback/*"          element={<FeedbackLayout/>}/>
                  <Route path="/enterprise/*"        element={<EnterpriseLayout/>}/>
                  <Route path="/tenders/*"           element={<Suspense fallback={<PageFallback/>}><TendersLayout/></Suspense>}/>

                  <Route path="/m"                   element={<MobileRoute><MobileApp/></MobileRoute>}/>
                  <Route path="*"                    element={<Navigate to="/" replace/>}/>
                </Routes>
              </Suspense>
              </ErrorBoundary>
            </PrivateRoute>
          }/>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
